import React, { FunctionComponent, ReactNode, useState } from 'react'

import styled, { useTheme } from 'styled-components/native'

import { BodyText, Size as BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { MenuDotsIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/MenuDotsIcon'
import { Menu, MenuItemProps } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/menu/Menu'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { NpTestPlanMeasureRow } from './NpTestPlanMeasureRow'
import { NpTestPlanMeasure } from '../data/npTestPlanTypes'

export interface NpTestPlanMeasureGroupProps {
  title: string
  subtitle?: string
  /** Rendered on the right of the header, e.g. the informant's contacts and actions. */
  headerAccessory?: ReactNode
  menuItems?: MenuItemProps[]
  isMenuDisabled?: boolean
  measures: NpTestPlanMeasure[]
  footer?: ReactNode
  onRemoveMeasure?: (measure: NpTestPlanMeasure) => void
  isRemoveDisabled?: boolean
  testIdName: string
}

// Unclipped and raised while the menu is open, so its dropdown can overlap the rows and cards below.
const Card = styled.View<{ theme: ThemeType; elevated: boolean }>(({ theme: { colors }, elevated }) => ({
  ...(elevated && { zIndex: 1 }),
  borderWidth: 1,
  borderColor: colors.borderDefault,
  borderStyle: 'solid',
  borderRadius: '16px',
  backgroundColor: colors.backgroundPrimary,
}))

const Header = styled.View<{ theme: ThemeType; elevated: boolean }>(({ theme: { colors, spacing }, elevated }) => ({
  ...(elevated && { zIndex: 1 }),
  borderTopLeftRadius: '15px',
  borderTopRightRadius: '15px',
  flexDirection: 'row',
  alignItems: 'center',
  gap: spacing['24px'],
  minHeight: '72px',
  paddingVertical: spacing['16px'],
  paddingHorizontal: spacing['16px'],
  backgroundColor: colors.backgroundSection,
}))

const HeaderTitle = styled.View({
  flex: 1,
})

const Footer = styled.View<{ theme: ThemeType }>(({ theme: { colors, spacing } }) => ({
  justifyContent: 'center',
  minHeight: '72px',
  paddingVertical: spacing['16px'],
  paddingHorizontal: spacing['16px'],
  borderTopWidth: 1,
  borderTopColor: colors.borderDefault,
  borderStyle: 'solid',
}))

export const NpTestPlanMeasureGroup: FunctionComponent<NpTestPlanMeasureGroupProps> = ({
  title,
  subtitle,
  headerAccessory,
  menuItems,
  isMenuDisabled,
  measures,
  footer,
  onRemoveMeasure,
  isRemoveDisabled,
  testIdName,
}) => {
  const { colors } = useTheme() as ThemeType
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  return (
    <Card testID={tID(testIdName)} elevated={isMenuOpen}>
      <Header elevated={isMenuOpen}>
        <HeaderTitle>
          <BodyText bold size={BodyTextSize.LARGE} text={title} />
          {Boolean(subtitle) && <BodyText size={BodyTextSize.SMALL} color={colors.textSecondary} text={subtitle} />}
        </HeaderTitle>
        {headerAccessory}
        {Boolean(menuItems?.length) && (
          <Menu
            testID={tID(`${testIdName}-menu`)}
            customMenuIcon={<MenuDotsIcon vertical />}
            disableBottomSheetForWeb
            horizontalPosition='right'
            menuItems={menuItems ?? []}
            disabled={isMenuDisabled}
            onOpenChange={setIsMenuOpen}
          />
        )}
      </Header>
      {measures.map((measure) => (
        <NpTestPlanMeasureRow
          key={measure.id}
          measure={measure}
          onRemove={onRemoveMeasure && (() => onRemoveMeasure(measure))}
          isRemoveDisabled={isRemoveDisabled}
        />
      ))}
      {Boolean(footer) && <Footer>{footer}</Footer>}
    </Card>
  )
}
